/* eslint-disable react/jsx-props-no-spreading */
import {Button, Input, Checkbox, useModals, Modal} from 'letrus-ui';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Controller, useFormContext} from 'react-hook-form';
import {validateEmail} from 'utils/functions/validation/email';
import {cpfMask} from 'utils/functions/validation/mask';
import {validateCPF} from 'utils/functions/validation/cpf';
import {formatDate} from 'utils/functions/validation/date';
import Select from 'react-select';
import {Term} from 'APIs/letrusAPI/terms/types';
import {Link} from 'react-router-dom';
import Terms from '../../../../components/Terms';
import styles from './ConfirmOlympicsUserInfo.module.scss';
import {ContestSignupForm} from '../..';

interface ConfirmOlympicsUserInfoProps {
  onGoBack(): void;
}

function ConfirmOlympicsUserInfo({onGoBack}: ConfirmOlympicsUserInfoProps) {
  const form = useFormContext<ContestSignupForm>();
  const modals = useModals(['terms']);

  const birthDate = form.watch('birth_date');
  const isUnderAge = birthDate && new Date(birthDate) > new Date('2014-05-26');

  const isInvalidForm =
    (isUnderAge &&
      (!form.watch('guardian_cpf') ||
        !form.watch('guardian_name') ||
        !form.watch('guardian_email') ||
        !form.watch('guardian_relationship') ||
        !form.watch('is_legal_guardian'))) ||
    !form.watch('terms_use_version_id') ||
    !form.watch('terms_privacy_version_id') ||
    !form.watch('accept_terms');

  function getTermsVersions(terms?: Term[]) {
    terms?.forEach((term) => {
      if (term.parameter === 'terms_use_text') {
        form.setValue('terms_use_version_id', term.id);
      }

      if (term.parameter === 'terms_student_contest_text') {
        form.setValue('terms_privacy_version_id', term.id);
      }
    });
  }

  return (
    <>
      <div className={styles.wrapper}>
        <h2>Confira seus dados antes de finalizar</h2>

        <div className={styles.field}>
          <FontAwesomeIcon icon={['fad', 'user']} />
          <div className={styles.fieldContent}>
            <span className={styles.fieldName}>Nome completo</span>
            <span className={styles.fieldValue}>
              {`${form.getValues('first_name')} ${form.getValues('last_name')}`}
            </span>
          </div>
        </div>

        <div className={styles.field}>
          <FontAwesomeIcon icon={['fad', 'calendar']} />
          <div className={styles.fieldContent}>
            <span className={styles.fieldName}>Data de nascimento</span>
            <span className={styles.fieldValue}>{formatDate(birthDate)}</span>
          </div>
        </div>

        <div className={styles.field}>
          <FontAwesomeIcon icon={['fad', 'phone']} />
          <div className={styles.fieldContent}>
            <span className={styles.fieldName}>WhatsApp</span>
            <span className={styles.fieldValue}>
              {form.getValues('phone_number')}
            </span>
          </div>
        </div>

        <div className={styles.field}>
          <FontAwesomeIcon icon={['fad', 'envelope']} />
          <div className={styles.fieldContent}>
            <span className={styles.fieldName}>E-mail</span>
            <span className={styles.fieldValue}>{form.getValues('email')}</span>
          </div>
        </div>

        {isUnderAge && (
          <div className={styles.guardianData}>
            <h3>Dados do responsável</h3>
            <Input
              id="guardian_name"
              label="Nome"
              {...form.register('guardian_name', {
                required: 'Nome é obrigatório',
              })}
              errorMessage={form.formState.errors.guardian_name?.message}
            />

            <Input
              id="guardian_cpf"
              label="CPF"
              mask={cpfMask}
              {...form.register('guardian_cpf', {
                required: 'CPF é obrigatório',
                validate: (value) => validateCPF(value),
              })}
              errorMessage={form.formState.errors.guardian_cpf?.message}
            />

            <Input
              id="guardian_email"
              label="E-mail"
              type="email"
              {...form.register('guardian_email', {
                required: 'E-mail é obrigatório',
                validate: (value) => validateEmail(value),
              })}
              errorMessage={form.formState.errors.guardian_email?.message}
            />

            <Controller
              name="guardian_relationship"
              control={form.control}
              rules={{required: 'Grau de parentesco é obrigatório'}}
              render={({field}) => (
                <Select
                  options={[
                    {value: 'father', label: 'Pai'},
                    {value: 'mother', label: 'Mãe'},
                    {value: 'legal_guardian', label: 'Responsável legal'},
                  ]}
                  placeholder="Selecione o grau de parentesco"
                  {...field}
                  onChange={(option) => field.onChange(option?.value)}
                />
              )}
            />

            <Checkbox
              id="is_legal_guardian"
              className={styles.overrideCheckbox}
              label="Declaro que sou responsável legal pelo estudante e autorizo sua participação na Olimpíada Linguagem em Ação, conforme os termos apresentados."
              data-testid="is-legal-guardian-checkbox"
              {...form.register('is_legal_guardian', {required: true})}
            />
          </div>
        )}

        <Checkbox
          id="accept_terms"
          className={styles.overrideCheckbox}
          label={
            (
              <span className={styles.termsLabel}>
                Declaro que Li e aceito os
                <button type="button" onClick={() => modals.terms?.showModal()}>
                  Termos de Uso
                </button>
                e o{' '}
                <Link
                  to="https://drive.google.com/file/d/1d5rXHyaqWLatv4ZovCaQVZZzRZoYPQ4i/view"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Regulamento da Olimpíada
                </Link>
                , e autorizo o recebimento de comuncações da Letrus por e-mail e
                WhatsApp, podendo cancelar essa autorização a qualquer momento.
              </span>
            ) as unknown as string // TODO: change letrus-ui to accept string or ReactNode in label
          }
          data-testid="accept-terms-checkbox"
          {...form.register('accept_terms', {
            required: 'É necessário aceitar os termos de uso',
          })}
        />
      </div>

      <div className={styles.controls}>
        <Button type="button" variant="secondary" onClick={onGoBack}>
          <FontAwesomeIcon icon="arrow-left" /> Voltar
        </Button>

        <Button type="submit" disabled={isInvalidForm}>
          <FontAwesomeIcon icon="check" />
          Confirmar
        </Button>
      </div>

      <Modal id="terms" closeButton onClick={() => modals.terms?.close()}>
        <Terms onTermsFetch={(terms) => getTermsVersions(terms)} />
      </Modal>
    </>
  );
}

export default ConfirmOlympicsUserInfo;
